import { Link, useRouter } from '@tanstack/react-router'
import { Calendar, MapPin, Users } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { EventStageEnum } from '@/gen/cortado/v1/settings_pb'
import { formatEnumName, getEventStageColor, eventStageFromName } from '@/lib/enum-utils'
import { formatTimestamp } from '@/lib/date-utils'
import type { RenderedEvent } from './render-data-payload'

/**
 * Clickable event card rendered inline in the chat transcript for
 * `render_data`. Markup mirrors `dashboard/lead-card.tsx`'s `renderInquiryItem`:
 * a bordered row linking to the event detail page, hover-preloaded, with a
 * stage badge.
 */
export function EventEntityCard({ event }: { event: RenderedEvent }) {
  const router = useRouter()
  const stage = eventStageFromName(event.stage)
  const eventDate = formatTimestamp(new Date(event.eventDate))

  const handlePrefetch = () => {
    void router.preloadRoute({ to: '/events/$id', params: { id: event.id } })
  }

  return (
    <Link
      to="/events/$id"
      params={{ id: event.id }}
      data-slot="entity-card"
      data-entity-type="event"
      className="block rounded-lg border p-3 hover:bg-muted/50 transition-colors"
      onMouseEnter={handlePrefetch}
    >
      <div className="flex items-start justify-between">
        <div className="space-y-1 min-w-0 flex-1">
          <p className="font-medium text-sm truncate">{event.contactName}</p>
          <div className="space-y-0.5 text-xs text-muted-foreground">
            {eventDate && (
              <div className="flex items-center gap-1.5 min-w-0">
                <Calendar className="h-3 w-3 flex-shrink-0" />
                <span className="truncate">{eventDate}</span>
              </div>
            )}
            {event.eventLocation && (
              <div className="flex items-center gap-1.5 min-w-0">
                <MapPin className="h-3 w-3 flex-shrink-0" />
                <span className="truncate">{event.eventLocation}</span>
              </div>
            )}
            {event.guestCount > 0 && (
              <div className="flex items-center gap-1.5 min-w-0">
                <Users className="h-3 w-3 flex-shrink-0" />
                <span className="truncate">{event.guestCount} guests</span>
              </div>
            )}
          </div>
        </div>
        <Badge variant={getEventStageColor(stage)}>
          {formatEnumName(stage, EventStageEnum)}
        </Badge>
      </div>
    </Link>
  )
}
